import React, { useState, useEffect } from 'react';
import { InvoicesProps, Invoice } from '../../../types/expertise-detail.types';
import { getAffaireFactures, downloadFile } from '../../../api/expertise-detail.api';
import { formatDate, formatCurrency } from '../../../utils/formatters';
import { Button } from '../../../../../../../ui/components/atoms/Button';
import { Loading } from '@/ui/components/atoms/Loading';

export const Invoices: React.FC<InvoicesProps> = ({ affaireId }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInvoices();
  }, [affaireId]);

  const fetchInvoices = async () => {
    if (!affaireId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAffaireFactures(affaireId);
      
      if (response.success && response.data) {
        setInvoices(response.data.factures || []);
      } else {
        setInvoices([]);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Erreur lors du chargement des factures');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoice: Invoice) => {
    if (!invoice.downloadUrl || !invoice.fileName) {
      setError('Informations de téléchargement non disponibles');
      return;
    }

    setDownloading(prev => new Set(prev).add(invoice.id));
    setError(null);

    try {
      await downloadFile(invoice.downloadUrl, invoice.fileName);
    } catch (err) {
      console.error('Error downloading invoice:', err);
      setError(`Erreur lors du téléchargement de la facture`);
    } finally {
      setDownloading(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id);
        return newSet;
      });
    }
  };

  const getTotalAmount = () => {
    return invoices.reduce((sum, invoice) => sum + (invoice.montantHTVA || 0), 0);
  };

  if (loading) {
    return (
      <div className="invoices-container loading">
        <Loading size="medium" text="Chargement des factures..." />
      </div>
    );
  }

  return (
    <div className="invoices-container">
      <div className="invoices-header">
        <h3>Factures</h3>
        <Button 
          variant="ghost" 
          size="small" 
          onClick={fetchInvoices} 
          title="Actualiser"
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M21 2v6h-6M3 12a9 9 0 0115-6.7L21 8M3 22v-6h6M21 12a9 9 0 01-15 6.7L3 16" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
        />
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
        </div>
      )}

      {invoices.length > 0 ? (
        <>
          <div className="invoices-list">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="invoice-card">
                <div className="invoice-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <rect x="5" y="4" width="14" height="16" rx="2" stroke="#ef5343" strokeWidth="2"/>
                    <path d="M9 9h6M9 13h6M9 17h3" stroke="#ef5343" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="invoice-info">
                  <div className="invoice-main">
                    <h4>{invoice.libelleFacture || 'Facture'}</h4>
                  </div>
                  <div className="invoice-details">
                    {invoice.fileName && (
                      <span className="invoice-filename">{invoice.fileName}</span>
                    )}
                    {invoice.dateFacture && (
                      <span className="invoice-date">Émise le {formatDate(invoice.dateFacture)}</span>
                    )}
                  </div>
                </div>
                <div className="invoice-action">
                  {invoice.available ? (
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => handleDownload(invoice)}
                      disabled={downloading.has(invoice.id)}
                      loading={downloading.has(invoice.id)}
                      title="Télécharger la facture"
                      className="icon-only-button"
                      icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" 
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      }
                    />
                  ) : (
                    <span className="status-badge unavailable">Non disponible</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="invoices-summary">
            <div className="summary-row">
              <span className="summary-label">Total HTVA</span>
              <span className="summary-value">{formatCurrency(getTotalAmount())}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">TVA (21%)</span>
              <span className="summary-value">{formatCurrency(getTotalAmount() * 0.21)}</span>
            </div>
            <div className="summary-row total">
              <span className="summary-label">Total TTC</span>
              <span className="summary-value">{formatCurrency(getTotalAmount() * 1.21)}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <rect x="5" y="4" width="14" height="16" rx="2" stroke="#9CA3AF" strokeWidth="2"/>
              <path d="M12 9v6M9 12h6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <p>Aucune facture disponible</p>
          <p className="hint">Les factures apparaîtront ici une fois émises</p>
        </div>
      )}

      <style>{`
        .invoices-container {
          padding: 28px;
          background: white;
          border-radius: 20px;
          border: 1px solid #E5E7EB;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
        }

        .invoices-container.loading {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 200px;
        }


        .invoices-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .invoices-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #374151;
        }

        .refresh-button {
          padding: 4px 8px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .refresh-button:hover {
          background: #F3F4F6;
          transform: rotate(180deg);
        }

        .error-message {
          padding: 12px;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 6px;
          color: #DC2626;
          font-size: 14px;
          margin-bottom: 16px;
        }

        .invoices-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }

        .invoice-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 20px;
          background: linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%);
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .invoice-card:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transform: translateX(2px);
        }

        .invoice-icon {
          font-size: 24px;
          flex-shrink: 0;
        }

        .invoice-info {
          flex: 1;
        }

        .invoice-main {
          margin-bottom: 4px;
        }

        .invoice-info h4 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #002035;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .invoice-details {
          display: flex;
          gap: 12px;
          font-size: 12px;
          color: #6B7280;
        }

        .invoice-filename {
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .invoice-action {
          flex-shrink: 0;
        }
        
        .icon-only-button {
          padding: 8px !important;
          min-width: 36px !important;
          width: 36px !important;
          height: 36px !important;
        }
        
        .icon-only-button .chatapp-button__icon {
          margin: 0 !important;
        }

        .download-button {
          padding: 6px 10px;
          background: white;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 16px;
        }

        .download-button:hover:not(:disabled) {
          background: #10B981;
          border-color: #10B981;
          transform: translateY(-1px);
        }

        .download-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }

        .status-badge.unavailable {
          background: #F3F4F6;
          color: #6B7280;
        }

        .invoices-summary {
          padding: 16px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          font-size: 14px;
        }

        .summary-row.total {
          padding-top: 12px;
          margin-top: 8px;
          border-top: 2px solid #E5E7EB;
          font-weight: 600;
          font-size: 16px;
        }

        .summary-label {
          color: #6B7280;
        }

        .summary-value {
          color: #111827;
          font-weight: 500;
        }

        .summary-row.total .summary-value {
          color: #10B981;
          font-weight: 700;
        }

        .empty-state {
          padding: 60px 20px;
          text-align: center;
          background: #FAFBFC;
          border: 2px dashed #E5E7EB;
          border-radius: 12px;
        }

        .empty-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .empty-state p {
          margin: 0;
          color: #6B7280;
          font-size: 14px;
        }

        .hint {
          font-size: 13px;
          color: #9CA3AF;
          margin-top: 8px;
        }

        @media (max-width: 768px) {
          .invoice-card {
            flex-direction: column;
            align-items: flex-start;
          }

          .invoice-main {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .invoice-details {
            flex-direction: column;
            gap: 4px;
          }

          .download-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};